import { createContext, useContext } from 'react'
import type {
  BrowserCaptureGuidance,
  BrowserRemedyGuidance,
} from '@/lib/extensionUtils'

export type PresentCaptureFlowPayload = {
  rationale: string
  testId: string
  title?: string
  entryUrl?: string
  frames: BrowserCaptureGuidance['frames']
  instructions?: string
  fallbackInstructions: string
}

export type PresentRemedyFlowPayload = {
  rationale: string
  testId?: string
  remediationTicketId?: string
  title: string
  entryUrl: string
  riskWarning: string
  steps: BrowserRemedyGuidance['steps']
  fallbackInstructions: string
}

export type CaptureContext = {
  workspaceId: string
  testId: string | null
  controlDefId: string | null
  testTitle: string | null
  taskCode: string | null
  controlRefs: string | null
}

export type CaptureFlowContextValue = {
  extensionInstalled: boolean | null
  /** Tool-call id currently starting a capture, or `null` when idle. */
  captureStartingId: string | null
  /** Tool-call id currently starting a remedy, or `null` when idle. */
  remedyStartingId: string | null
  captureContext: CaptureContext
  startCapture: (
    payload: PresentCaptureFlowPayload,
    toolCallId: string
  ) => Promise<void>
  startRemedy: (
    payload: PresentRemedyFlowPayload,
    toolCallId: string
  ) => Promise<void>
}

export const CaptureFlowContext = createContext<CaptureFlowContextValue | null>(
  null
)

export function useCaptureFlow(): CaptureFlowContextValue | null {
  return useContext(CaptureFlowContext)
}

export function formatCaptureDestinationLabel(params: {
  flowTestId: string
  contextTestId: string | null
  taskCode: string | null
  testTitle: string | null
  controlRefs: string | null
}): string {
  const { flowTestId, contextTestId, taskCode, testTitle, controlRefs } = params
  const contextMatches = !contextTestId || contextTestId === flowTestId

  if (!contextMatches) {
    const shortId =
      flowTestId.length > 12 ? `${flowTestId.slice(0, 8)}…` : flowTestId
    return shortId
  }

  const code = taskCode?.trim() || null
  const title = testTitle?.trim() || null
  const controls =
    controlRefs
      ?.split(',')
      .map((ref) => ref.trim())
      .filter(Boolean) ?? []

  if (code && controls.length > 0) {
    return controls.length === 1
      ? `${code} (${controls[0]})`
      : `${code} (${controls.join(', ')})`
  }
  if (code && title) return `${code} · ${title}`
  if (code) return code
  if (title) return title

  const shortId =
    flowTestId.length > 12 ? `${flowTestId.slice(0, 8)}…` : flowTestId
  return shortId
}
